<script setup lang="ts">
    import { reactive, computed, onMounted, watch } from 'vue';
    import { getIssues, getCountPerIssueStatus } from './Issues.access';
    import { type Issue, statusName, IssueSourceFilter, OrderByValue, type CountPerIssueStatus } from './interfaces';
    import { orderOptions } from './Issues';
    import { OrderByDirection, type tabs } from '@shared/components/List/ListTypes';
    import { setupTags } from '@shared/components/Tags/Tags';
    import List from '@shared/components/List/List.vue';
    import ListItem from './ListItem.vue';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';

    const emit = defineEmits<{
        (ev: 'clickedIssueId', id: string): void;
        (ev: 'hasItems', value: boolean): void;
    }>();

    const props = withDefaults(
        defineProps<{
            onlyTemplates?: boolean;
            hideTemplates?: boolean;
            selectedId?: string | null;
        }>(),
        {}
    );

    const pageSize = 15;
    const tags = setupTags();

    const state = reactive<{
        issues: Issue[];
        loading: boolean;
        allLoaded: boolean;
        page: number;
        currentFilter: string;
        currentStatus: statusName;
        orderByValue: OrderByValue;
        orderByDirection: OrderByDirection;
        abortController: AbortController | null;
        tabs: tabs;
    }>({
        issues: [],
        loading: false,
        allLoaded: false,
        page: 0,
        currentFilter: '',
        currentStatus: props.onlyTemplates ? statusName.template : statusName.all,
        orderByValue: OrderByValue.LastModified,
        orderByDirection: 'desc',
        abortController: null,
        tabs: {
            [statusName.all]: { name: statusName.all, display: $Resources.ToggleOptionsAll },
            [statusName.draft]: { name: statusName.draft, display: $Resources.DraftLabel },
            [statusName.issueVariation]: { name: statusName.issueVariation, display: $Resources.IssueVariationTab, hidden: true },
            [statusName.scheduled]: { name: statusName.scheduled, display: $Resources.ScheduledLabel, hidden: true },
            [statusName.recurring]: { name: statusName.recurring, display: $Resources.IssuesRecurringHeading, hidden: true },
            [statusName.paused]: { name: statusName.paused, display: $Resources.IssuesPausedTab, hidden: true },
            [statusName.sending]: { name: statusName.sending, display: $Resources.IssuesSendingHeading, hidden: true },
            [statusName.sent]: { name: statusName.sent, display: $Resources.SentLabel },
            [statusName.failed]: { name: statusName.failed, display: $Resources.IssuesFailedTab, hidden: true },
        },
    });

    const tabsForList = computed(() => (props.onlyTemplates ? undefined : state.tabs));

    const updateTabsToShow = (counts: CountPerIssueStatus) => {
        const recurringCount = counts.RecurringIssuesCount - counts.ScheduledIssuesCount;
        if (counts.IssueVariationCount > 0) state.tabs[statusName.issueVariation].hidden = false;
        if (counts.ScheduledIssuesCount > 0) state.tabs[statusName.scheduled].hidden = false;
        if (recurringCount > 0) state.tabs[statusName.recurring].hidden = false;
        if (counts.PausedIssuesCount > 0) state.tabs[statusName.paused].hidden = false;
        if (counts.SendingIssuesCount > 0) state.tabs[statusName.sending].hidden = false;
        if (counts.FailedIssuesCount > 0) state.tabs[statusName.failed].hidden = false;
    };

    onMounted(async () => {
        if (!props.onlyTemplates) {
            const counts = await getCountPerIssueStatus();
            updateTabsToShow(counts);
        }
        loadIssues();
    });

    watch(
        () => tags.state.selected,
        () => {
            state.page = 0;
            state.issues = [];
            loadIssues();
        },
        { deep: true }
    );

    const loadIssues = () => {
        state.loading = true;
        state.allLoaded = false;

        if (state.abortController) {
            state.abortController.abort();
        }
        state.abortController = new AbortController();

        let aborted = false;

        getIssues(
            {
                status: state.currentStatus,
                page: state.page,
                pageSize,
                currentFilter: state.currentFilter,
                fromAddress: '',
                sortOrder: state.orderByValue,
                orderByDirection: state.orderByDirection,
                tagIds: tags.state.selected?.map((t) => t.TagId) || [],
                categoryIds: [],
                start: null,
                end: null,
                sourceFilter: IssueSourceFilter.None,
            },
            { signal: state.abortController.signal }
        )
            .then((result: Issue[]) => {
                if (state.page === 0) {
                    state.issues = result || [];
                    if (props.onlyTemplates) {
                        emit('hasItems', state.issues.length > 0);
                    }
                } else {
                    state.issues = [...state.issues, ...(result || [])];
                }
                state.allLoaded = !result || result.length < pageSize;
            })
            .catch((error: Error) => {
                if (error.name === 'AbortError') {
                    aborted = true;
                } else {
                    bsAlert({ type: AlertTypes.Danger });
                }
            })
            .finally(() => {
                if (!aborted) {
                    state.loading = false;
                    state.abortController = null;
                }
            });
    };

    const resetAndLoad = () => {
        state.page = 0;
        state.issues = [];
        loadIssues();
    };

    const switchStatus = (newStatus: string) => {
        const newStatusName = newStatus as statusName;
        if (state.currentStatus === newStatusName) return;
        state.currentStatus = newStatusName;

        if (newStatusName === statusName.sent) {
            state.orderByValue = OrderByValue.StartedDateTime;
            state.orderByDirection = 'desc';
        } else {
            state.orderByValue = OrderByValue.LastModified;
            state.orderByDirection = 'desc';
        }

        resetAndLoad();
    };

    const onFilterUpdate = (val: string) => {
        state.currentFilter = val;
        resetAndLoad();
    };

    const handleUpdateOrder = (orderByValue: string, orderByDirection: OrderByDirection) => {
        state.orderByValue = orderByValue as OrderByValue;
        state.orderByDirection = orderByDirection;
        resetAndLoad();
    };
</script>

<template>
    <List
        :items="state.issues"
        itemId="IssueId"
        :currentTab="state.currentStatus"
        :tabs="tabsForList"
        :loading="state.loading"
        :allLoaded="state.allLoaded"
        :selectedId="selectedId"
        :serverSideFilter="true"
        :filterRateLimit="300"
        :tags="tags"
        :orderOptions="orderOptions(state.currentStatus)"
        :orderByValue="state.orderByValue"
        :orderByDirection="state.orderByDirection"
        hideHeader
        @update:tab="switchStatus"
        @update:filter="onFilterUpdate"
        @update:order="handleUpdateOrder"
        @loadMore="
            () => {
                state.page += 1;
                loadIssues();
            }
        ">
        <template #item="{ item }">
            <ListItem
                :issue="item"
                :currentTab="state.currentStatus"
                :loading="state.loading"
                :isCreateNew="true"
                :tags="tags"
                @clickedIssueId="emit('clickedIssueId', item.IssueId)"></ListItem>
        </template>
    </List>
</template>
